import { Content, List, Root, Trigger } from '@radix-ui/react-tabs';
import * as stylex from '@stylexjs/stylex';
import { clsx } from 'clsx';
import Icon from '../Icon';
import { colors } from '../theme/colors.stylex';
import type { TabsProps } from './Tabs.types';

const styles = stylex.create({
  root: {
    display: 'flex',
    maxWidth: '100%',
    minWidth: '18.75rem',
    flexGrow: 1,
    flexDirection: 'column',
    outlineStyle: { 'default': null, ':focus-visible': 'none' },
  },
  list: { zIndex: 1001, display: 'flex', flexDirection: 'row', flexWrap: 'wrap' },
  trigger: {
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    lineHeight: 'inherit',
    color: 'inherit',
    paddingBlock: 0,
    cursor: 'pointer',
    display: 'flex',
    minWidth: '6.25rem',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 0,
    borderRightWidth: { 'default': '2px', ':last-child': 0 },
    borderStyle: 'solid',
    borderColor: colors.tabBorder,
    backgroundColor: { 'default': colors.background, ':hover': colors.tabHover },
    paddingInline: '1rem',
    borderBottomWidth: '0.0625rem',
    outlineStyle: { 'default': null, ':focus-visible': 'none' },
  },
  fit: { maxWidth: 'max-content', minWidth: 'fit-content' },
  active: {
    borderBottomWidth: '0.1875rem',
    borderBottomColor: colors.accentPressed,
    backgroundColor: { 'default': colors.background, ':hover': colors.background },
  },
  labelContainer: { display: 'flex', alignItems: 'center', overflow: 'hidden' },
  icon: { fontSize: '0.9375rem', marginRight: '0.4375rem', flexShrink: 0 },
  label: {
    overflow: 'hidden',
    fontSize: '0.8125rem',
    fontWeight: 500,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: colors.tabLabel,
  },
  activeLabel: { color: colors.tabLabelActive },
  content: {
    zIndex: 500,
    marginTop: '-0.0625rem',
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    overflowY: 'auto',
    borderTopWidth: '0.0625rem',
    borderTopStyle: 'solid',
    borderTopColor: colors.tabBorder,
    backgroundColor: colors.background,
    outlineStyle: { 'default': null, ':focus-visible': 'none' },
  },
});

const Tabs = ({
  tabs,
  defaultActiveTab,
  activeTab,
  onTabSelect,
  extraClassNames,
  xstyle,
  testId,
  id,
  stretchTabs = false,
}: TabsProps) => {
  const handleTabSelect = (tabId: string) => {
    if (activeTab === tabId) return;
    onTabSelect && onTabSelect(tabId);
  };

  const rootProps = stylex.props(styles.root, xstyle);
  return (
    <Root
      {...rootProps}
      className={clsx(rootProps.className, extraClassNames)}
      defaultValue={defaultActiveTab}
      data-testid={testId}
      id={id}
      value={activeTab}
      onValueChange={handleTabSelect}>
      <List {...stylex.props(styles.list)}>
        {tabs.map((tab, index) => {
          const triggerProps = stylex.props(
            styles.trigger,
            !stretchTabs && styles.fit,
            activeTab === tab.id && styles.active,
            tab.tabXstyle,
          );
          const labelProps = stylex.props(styles.label, activeTab === tab.id && styles.activeLabel, tab.tabLabelXstyle);
          return (
            <Trigger
              {...triggerProps}
              className={clsx(triggerProps.className, tab.tabExtraClassNames)}
              data-testid={tab.testId}
              disabled={tab.disabled}
              key={`${tab.label}-${tab.id}-${index}`}
              value={tab.id}>
              <span {...stylex.props(styles.labelContainer)}>
                {tab.icon && <Icon icon={tab.icon} testId={`${tab.id}_tab-icon`} xstyle={styles.icon} />}
                <span {...labelProps} className={clsx(labelProps.className, tab.tabLabelExtraClassNames)}>
                  {tab.label}
                </span>
              </span>
            </Trigger>
          );
        })}
      </List>
      {tabs.map((tab, index) => {
        const contentProps = stylex.props(styles.content, tab.tabContentXstyle);
        return (
          <Content
            {...contentProps}
            className={clsx(contentProps.className, tab.tabContentExtraClassNames)}
            key={`${tab.label}_${index}_content`}
            value={tab.id}>
            {tab.content}
          </Content>
        );
      })}
    </Root>
  );
};

export default Tabs;
